import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { GoldButton, GhostButton } from "@/components/Reveal";
import { WHATSAPP_LINK, scrollToId } from "@/constants/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1567601169793-64703dc5324a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const MaskedLine = ({ text, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="grain relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Cinematic Himalayan landscape at dusk"
          className="animate-slow-zoom h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C0D0F]/80 via-[#0C0D0F]/55 to-[#0C0D0F]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C0D0F]/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8 lg:px-16">
        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, letterSpacing: "0.6em" }}
          animate={{ opacity: 1, letterSpacing: "0.35em" }}
          transition={{ duration: 1.4, delay: 0.2 }}
          className="mb-8 text-[11px] font-mono uppercase text-gold"
        >
          Every Journey. Personally Managed.
        </motion.p>

        <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-tight text-zinc-50 sm:text-7xl lg:text-8xl">
          <MaskedLine text="Your Journey." delay={0.45} />
          <MaskedLine text="Personally" delay={0.6} className="italic text-gold-gradient" />
          <MaskedLine text="Managed." delay={0.75} />
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-zinc-300 sm:text-lg"
        >
          Tell us where you want to go, how you want to experience it, and what you'd like to spend.
          We'll design and coordinate the journey around you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <GoldButton testId="hero-plan-cta" onClick={() => scrollToId("plan")}>
            Plan My Journey
          </GoldButton>
          <GhostButton testId="hero-whatsapp-cta" href={WHATSAPP_LINK}>
            Chat With Us
          </GhostButton>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("budget")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 transition-colors hover:text-gold"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};
